/**
 * Starting the editor.
 *
 * Two ways in: mounted by WordPress into a div PHP printed, with its config on
 * window.trimboxConfig; or run straight from Vite during development, where
 * there is no WordPress and the config below stands in for it.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { setConfig, type BootConfig } from './api';
import { useEditor } from './state/store';
import { caretRect, layoutFor } from './text/layout';
import { imageFor, knownAsset } from './assets';
import { grid, moduleMm, payload } from './codes/qr';
import type { TextLayer } from './model/types';

// The store, reachable from a test runner. Read-only in spirit: nothing in the
// app uses it, and it exists so a headless check can set up a real card without
// forty simulated clicks.
(window as unknown as { __tbStore: typeof useEditor }).__tbStore = useEditor;

/**
 * Where the caret would sit for every character position in a text layer.
 *
 * Also for the test runner. Caret arithmetic in Arabic is the one thing here
 * that cannot be judged by looking at a screenshot - the letters are joined
 * and in visual order, so "is position 3 in the right place" is a question
 * only the numbers can answer.
 */
/**
 * Whether a picture has actually decoded.
 *
 * For the test runner. "The layer exists" and "the picture is on the card" are
 * different claims, and only the second is what a customer sees - a screenshot
 * caught this being untested.
 */
/**
 * QR encoding, for the test runner.
 *
 * Whether a code will scan off paper cannot be seen in a screenshot - the two
 * ways it fails, modules finer than the press can hold and more data than the
 * version holds, both look perfect on a monitor.
 */
(window as unknown as { __tbCodes: unknown }).__tbCodes = { grid, moduleMm, payload };

(window as unknown as { __tbAssets: unknown }).__tbAssets = {
  known: (id: string) => !!knownAsset(id),
  drawn: (id: string) => !!imageFor(id),
};

(window as unknown as { __tbLayout: unknown }).__tbLayout = {
  probeCarets: (layer: TextLayer) =>
    Array.from({ length: layer.text.length + 1 }, (_, i) => caretRect(layer, i)?.x ?? NaN),

  laid: (layer: TextLayer) => layoutFor(layer),

  /** The rightmost caret position, in mm from the layer's left edge. */
  rightEdge: (layer: TextLayer) =>
    Math.max(
      ...Array.from({ length: layer.text.length + 1 }, (_, i) => caretRect(layer, i)?.x ?? 0)
    ),
};

const mount = document.getElementById('trimbox-editor');

// A belt to go with the braces in vite.config.ts. If anything ever manages to
// load this bundle twice, the second copy must not mount a second React root
// over the first - two editors on one div, each with its own store, is a
// symptom nobody would diagnose from what it looks like on screen.
const already = mount?.hasAttribute('data-trimbox-mounted');

if (mount && !already) {
  mount.setAttribute('data-trimbox-mounted', '1');
  setConfig(window.trimboxConfig ?? developmentConfig());
  createRoot(mount).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}

/**
 * What the editor runs against with no WordPress behind it.
 *
 * A real business card, because a development fixture that is not a real size
 * hides every layout problem until the day it matters.
 */
function developmentConfig(): BootConfig {
  return {
    root: '/prismatize/wp-json',
    nonce: '',
    uuid: '',
    product: { id: 0, variation: 0, name: 'Business Cards · Art Matte 350' },
    size: { w: 90, h: 50, bleed: 3, safe: 3 },
    pages: [
      { id: 'front', name: 'Front' },
      { id: 'back', name: 'Back' },
    ],
    fonts: [
      { family: 'Cairo', url: '/fonts/', faces: [['Cairo.ttf', 400, 0]] },
      { family: 'Noto Naskh Arabic', url: '/fonts/', faces: [['NotoNaskhArabic.ttf', 400, 0]] },
    ],
    returnUrl: '/prismatize/product/business-cards/',
    addToCartUrl: '/prismatize/product/business-cards/',
  };
}
